import React, {useState} from 'react';
import PageWithHeader from "../common/PageWithHeader";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import {
  KnowledgeBaseEntry,
  useGetKnowledgeBaseQuery,
  useGetUsersMeQuery,
  usePostKnowledgeBaseMutation,
} from "../../redux/vmsApi";
import {Download, Visibility} from '@mui/icons-material';
import {styled} from "@mui/material/styles";
import config from "../../config";
import DropzoneArea from "../common/DropzoneArea";

const StyledCard = styled(Card)(() => ({
  display: 'flex',
  backgroundColor: '#1976d2',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  transition: '0.3s',
  borderRadius: '10px',
  height: '100%',
  '&:hover': {
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)',
  },
}));

const StyledTypography = styled(Typography)({
  textAlign: 'center',
  color: '#fff',
  fontSize: '1rem',
  lineHeight: 1.2,
});


const KnowledgeBase: React.FC = () => {
  const {data: knowledgeBaseEntries} = useGetKnowledgeBaseQuery();
  const {data: currentUser} = useGetUsersMeQuery();
  const [createKnowledgeBaseEntry, {isLoading: isSaving}] = usePostKnowledgeBaseMutation();
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [attachments, setAttachments] = useState<{name: string; url: string}[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isAdmin = currentUser?.roles?.includes('admin') ?? false;

  const handleDialogClose = () => setOpenDialog(null);

  const handleSaveRecord = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    const trimmedName = documentName.trim();
    if (!trimmedName) {
      setErrorMessage('Document name is required.');
      return;
    }

    if (attachments.length === 0) {
      setErrorMessage('Please upload a document before saving.');
      return;
    }

    const uploadedFile = attachments[0];
    const documentPath = uploadedFile.name.startsWith('procurement/')
      ? uploadedFile.name
      : `procurement/${uploadedFile.name}`;

    try {
      await createKnowledgeBaseEntry({
        newKnowledgeBaseEntry: {
          documentName: trimmedName,
          documentPath,
        },
      }).unwrap();

      setDocumentName('');
      setAttachments([]);
      setSuccessMessage('Knowledge base record uploaded successfully.');
    } catch (error) {
      setErrorMessage('Unable to save the knowledge base record. Please try again.');
    }
  };

  return (<PageWithHeader>
      <Paper elevation={2} sx={{margin: 10, padding: 5, borderRadius: '15px'}}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant='h1' gutterBottom sx={{fontWeight: 'bold', color: '#123456'}}>
              Knowledge Base
            </Typography>
          </Grid>
        </Grid>
        <Typography variant='body1' gutterBottom>
          Welcome to the Knowledge Base. Here, you'll find a collection of internal policies, guidelines, and important
          documents designed to help you navigate your role more effectively. Whether you’re looking for company
          procedures, compliance requirements, or best practices, this resource hub provides easy access to all the
          information you need.
        </Typography>

        {isAdmin && (
          <Card sx={{p: 3, mb: 4}}>
            <Typography variant="h6" gutterBottom>
              Upload Knowledge Base Record
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Document Name"
                value={documentName}
                onChange={(event) => setDocumentName(event.target.value)}
                fullWidth
              />
              <DropzoneArea
                key={attachments.map((attachment) => attachment.name).join('|') || 'empty-knowledge-base-upload'}
                setAttachments={setAttachments}
                fieldName="knowledgeBaseDocument"
                attachments={attachments}
              />
              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
              {successMessage && <Alert severity="success">{successMessage}</Alert>}
              <Button
                variant="contained"
                onClick={handleSaveRecord}
                disabled={isSaving || !documentName.trim() || attachments.length === 0}
                sx={{alignSelf: 'flex-start'}}
              >
                {isSaving ? 'Saving...' : 'Save Record'}
              </Button>
            </Stack>
          </Card>
        )}

        <Grid container spacing={3}>
          {knowledgeBaseEntries ? knowledgeBaseEntries.map((entry: KnowledgeBaseEntry) => (
            <Grid item xs={12} md={6} lg={4} key={entry.id}>
              <StyledCard>
                <CardContent sx={{ width: '100%', p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <StyledTypography variant="subtitle1" sx={{ textAlign: 'left' }}>
                      {entry.documentName}
                    </StyledTypography>
                    <Stack direction="row" spacing={1}>
                      <IconButton onClick={() => setOpenDialog(`${config.CDN_URL}/${entry.documentPath}`)} size="small">
                        <Visibility sx={{ fontSize: 22, color: '#fff' }} />
                      </IconButton>
                      <IconButton size="small">
                        <a
                          href={`${config.CDN_URL}/${entry.documentPath}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}
                        >
                          <Download sx={{ fontSize: 22, color: '#fff' }} />
                        </a>
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </StyledCard>
            </Grid>))
            :
            <CircularProgress sx={{margin: 10}}/>
          }
        </Grid>
      </Paper>
      <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
        <Box p={2}>
          {openDialog && (
            <iframe
              src={openDialog}
              title="File Viewer"
              width="100%"
              height="750px"
              style={{ border: 'none' }}
            />
          )}
        </Box>
      </Dialog>
    </PageWithHeader>)
}

export default KnowledgeBase;